<template>
    <div 
        class="smartform-formio-outer spl-formio-tight"
        :class="[isReplyMode ? 'smartform-reply-mode' : 'smartform-setup-mode']"
    >
        <div v-if="assetsLoaded" key="assetsLoadedContentArea">
            <LoadingContext :loading="isLoadingForm">
                <b-alert
                    :show="!!errorHandlingMessage"
                    variant="danger"
                    dismissible
                >
                    {{ errorHandlingMessage }}
                </b-alert>

                <!-- <div class="d-flex justify-content-between gap-4"> -->
                <div class="row">

                    <div class="col-12 col-lg-6">  <!-- LEFT SIDE -->
                        <div v-if="isReplyMode">
                            <h3>{{ formObj.name }}</h3>
                            <p v-if="formObj.instructions">{{ formObj.instructions }}</p>
                            <!-- TODO - add 'linked_products' read only here -->
                        </div>

                        <div v-else class="d-flex gap-4">  <!-- NOT REPLY MODE - SETUP MODE -->
                            <div class="flex-fill">
                                <b-form-group
                                    label="Quote Title:"
                                    label-cols="2"
                                    label-class="font-size-120 font-weight-bold"
                                >
                                    <b-form-input
                                        v-model="formObj.name"
                                        size="lg"
                                        :disabled="isReadOnly"
                                        lazy
                                    ></b-form-input>
                                </b-form-group>
                                <b-form-group
                                    label="Instructions:"
                                    label-cols="2"
                                    label-class="font-size-120 font-weight-bold"
                                >
                                    <b-form-textarea
                                        v-model="formObj.instructions"
                                        :disabled="isReadOnly"
                                        lazy
                                    ></b-form-textarea>
                                </b-form-group>
                                <!-- we only want to show the product autocomplete if that key is in formObj -->
                                <!-- TODO - make available for everyone soon! -->
                                <b-form-group
                                    v-if="formObj.linked_products"
                                    label="Part(s):"
                                    label-cols="2"
                                    label-class="font-size-120 font-weight-bold"
                                >
                                    <item-autocomplete
                                        v-model="formObj.linked_products"
                                        :multiple="true"
                                        :disabled="isReadOnly"
                                    ></item-autocomplete>
                                </b-form-group>
                                <!-- :disabled="isReadOnly"  :no-add="true" -->
                            </div>
                        </div>
                    </div>
                    <div class="col-12 col-lg-4">
                        <b-form-group
                            v-if="showGeneralFilesArea"
                            label="Files:"
                            label-cols="2"
                            label-class="font-size-120 font-weight-bold"
                        >
                            <BaseS3FileDownloadButtonGroup
                                v-if="isReplyMode"
                                :fileData="formObj.general_files"
                                vertical
                                size="sm"
                                :buttonProps="{ buttonVariant: 'outline-soft-primary' }"
                                bundleDownloadLocation="below"
                            ></BaseS3FileDownloadButtonGroup>
                            <FileSlingerListUploader
                                v-else
                                category="smartform_general"
                                :parent-id="formObj.id"
                                :disabled="isReplyMode || isReadOnly"
                                :existing-files="formObj.general_files"
                                small-list
                                no-preview
                                input-height="100px"
                                max-filesize="100mb"
                            ></FileSlingerListUploader>
                        </b-form-group>  <!-- maybe use different component on reply side to avoid expiration of URL auth querystrings? -->
                    </div>
                    <div class="col-12 col-lg-2">
                        <div class="d-flex flex-column gap-3">
                            <div v-if="showDownloadEmptyFormButton" class="">
                                <b-button 
                                    :href="downloadEmptyFormUrl" 
                                    size="md" 
                                    variant="subtle-secondary"
                                    class="d-flex gap-3 justify-content-center align-items-center"
                                >
                                    <span class="fiv-sqo fiv-icon-xlsx font-size-120"></span>
                                    <div class="">Download Excel Template</div>
                                </b-button>
                            </div>
                            <div class="vendor-input-section py-2 px-3 border rounded-lg font-size-80 text-center">
                                Vendors complete items highlighted in this color
                            </div>
                        </div>
                    </div>
                
                </div>

                <div class="row">

                    <div class="col">

                        <div
                            class=""
                            :class="{ 'quote-worksheet-portal': isReplyMode }"
                        >
                            <formio-form
                                ref="form"
                                v-if="schemaHash"
                                :key="schemaHash"
                                :schema="schema"
                                :submission-data="initialSubmissionData"
                                :formOptions="formOptions"
                                :class="formObj.extra_classes"
                                @change="onChange"
                                @blur="onBlur"
                                @is-valid="onIsValid"
                            ></formio-form>
                        
                            <div v-if="showInternalOnlyNote" class="d-flex">
                                <div class="internal-only-note p-2 rounded">Fields in this color are only visible to INTERNAL users (not vendors)</div>
                            </div>
                        </div>

                    </div>

                    <div v-if="showChildFormArea" class="col-3">
                        
                        <div class="">
                            <!-- CHILD FORM AREA - active in both builder/reply modes now -->
                            <div class="my-4">

                                <div class="">
                                    <!-- MODAL VERSION - used for setup and reply modes - common interface-->
                                    <div class="smartform-section-heading">
                                        <h4>{{ childFormsDisplayName }}</h4>
                                    </div>
                                    <div class="text-muted font-size-80 my-4">
                                        <span>{{ childFormsDescription }}</span>
                                    </div>
                                    <div 
                                        v-for="child in childForms" 
                                        :key="child.id" 
                                        class="px-3 py-75 mb-3 border rounded-lg shadow-sm overflow-hidden"
                                        :class="[child.bgClass, child.isValid ? '' : 'border-danger']"
                                    >
                                        <div class="smartform-child-form-toggle collapsed mb-3">
                                            <div class="toggle-inner d-flex justify-content-between align-items-center">
                                                <div class="text-left font-weight-bold font-size-90">
                                                    {{ child.name }}
                                                </div>
                                                <div class="badge-wrapper">
                                                    <!-- do not show badge when it is not-collapsed - because validity might be changing -->
                                                    <b-badge 
                                                        :variant="child.badgeVariant"
                                                        class="text-uppercase"
                                                    >
                                                        {{ child.badgeText }}
                                                    </b-badge>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="d-flex gap-2">
                                            <b-button
                                                v-if="child.showEditButton"
                                                :variant="child.editButtonVariant"
                                                class="flex-fill"
                                                @click="handleChildFormEdit(child)"
                                            >
                                                {{ child.editButtonText }}
                                                <b-icon v-if="child.editButtonIcon" :icon="child.editButtonIcon" class="ml-3"></b-icon>
                                            </b-button>
                                            <b-button
                                                v-if="child.showCreateButton"
                                                :variant="child.createButtonVariant"
                                                class="flex-fill"
                                                @click="handleChildFormReplyCreate(child)"
                                            >
                                                {{ child.createButtonText }}
                                                <b-icon v-if="child.createButtonIcon" :icon="child.createButtonIcon" class="ml-3"></b-icon>
                                            </b-button>
                                            <b-button
                                                v-if="child.showDeleteButton"
                                                variant="gray-danger"
                                                class="flex-fill"
                                                @click="deleteChildForm(child)"
                                            >
                                                <b-icon icon="trash" class=""></b-icon>
                                            </b-button>
                                        </div>
                                    </div>
                                    <b-modal
                                        v-model="childFormEditModalShowing"
                                        :key="`child-form-edit-modal-${openChildFormId}`"
                                        centered
                                        size="xxl"
                                        title="Quote Worksheet"
                                        body-class="bg-gray-100"
                                        content-class="overflow-hidden"
                                        no-close-on-esc
                                        no-close-on-backdrop
                                        hide-footer
                                        hide-header
                                        scrollable
                                        @hidden="resetChildFormEditModal"
                                    >
                                        <SmartFormFormio
                                            :form-id="isReplyMode ? undefined : openChildFormId"
                                            :reply-id="isReplyMode ? openChildReplyId : undefined"
                                            @update="handleChildUpdate"
                                            @request-close="closeChildFormEditModal"
                                        />  <!-- only pass the form OR reply ID depending on the mode  -->
                                    </b-modal>
                                </div>

                                <!-- <div :class="allChildFormsValid ? 'text-success' : 'text-danger'">
                                    {{ allChildFormsValid ? 'All Valid' : 'Incomplete' }}
                                </div> -->

                                <div v-if="!isReplyMode" class="my-3">
                                    <!-- ADD COMPONENT AREA - only show in SETUP mode -->
                                    <b-button 
                                        v-if="formObj.allow_child_forms && !isReadOnly"
                                        variant="subtle-primary"
                                        size="lg"
                                        block
                                        @click="showAddChildModal"
                                    >
                                        <b-icon icon="plus-circle"></b-icon>
                                        Add Component
                                    </b-button>
                                </div>
                            </div>
                            
                            <!-- MODAL FOR ADDING NEW CHILD FORMS - ONLY IN SETUP MODE -->
                            <b-modal
                                v-if="!isReplyMode"
                                v-model="addChildModalShowing"
                                title="Add Other Item"
                                centered
                                size="sm"
                                button-size="lg"
                                :ok-disabled="addChildOkDisabled"
                                @ok="addChild"
                                @hidden="resetAddChild"
                            >
                                <b-form-group
                                    label="Choose a template:"
                                    label-class="font-weight-bold"
                                    :description="`Choose which template to use.`"
                                    class="mt-3"
                                >
                                    <b-form-select
                                        v-model="addChildData.template_form"
                                        :options="templateOptions"
                                        value-field="id"
                                        text-field="name"
                                        autofocus
                                        size="lg"
                                    >
                                        <template #first>
                                            <b-form-select-option :value="''" disabled>
                                                {{ templateOptions.length ? '-- Select a template --' : 'No templates available' }}
                                            </b-form-select-option>
                                        </template>
                                    </b-form-select>
                                </b-form-group>
                                
                                <b-form-group
                                    label="Title"
                                    label-class="font-weight-bold"
                                    :description="`Provide a title for this Quote Worksheet.  Will be visible to vendors.  You can change it later.`"
                                    class="mt-3"
                                >
                                    <b-form-input
                                        v-model="addChildData.name"
                                        trim
                                        size="lg"
                                        @keyup.enter="addChild"
                                    ></b-form-input>
                                </b-form-group>
                            </b-modal>
                        </div>

                    </div>

                </div>

            </LoadingContext>
        </div>
        <div v-else key="assetsLoadingContent" class="min-height-300 d-flex align-items-center justify-content-center">
            <div class="text-center">
                <div class="mb-3">
                    <b-spinner label="Loading..."></b-spinner>
                </div>
                <div class="mb-3 font-size-110">Loading... Please wait.</div>
                <div class="d-flex gap-4 align-items-center justify-content-center font-size-70">
                    <div class="d-flex gap-2">
                        <b-icon :icon="isJsLoaded ? 'circle-check' : 'hourglass-split'"></b-icon>
                        <span>{{ isJsLoaded ? 'Logic Loaded' : 'Loading logic...' }}</span>
                    </div>
                    <div class="d-flex gap-2">
                        <b-icon :icon="isCssLoaded ? 'circle-check' : 'hourglass-split'"></b-icon>
                        <span>{{ isCssLoaded ? 'Styles Loaded' : 'Loading styles...' }}</span>
                    </div>
                </div>
            </div>
        </div>
        <hr>
        <div v-if="assetsLoaded" key="assetsLoadedButtonArea" class="">
            <div class="smartform-footer">
                <div class="d-flex justify-content-between w-100">
                    <div class="d-flex gap-4 align-items-center">
                        <div v-if="isReadOnly" class="d-flex gap-3 align-items-center">
                            <b-badge
                                variant="warning"
                                class="text-uppercase font-size-100"
                                >Read Only</b-badge
                            >
                            <span>{{ isReplyMode ? 'Already Submitted' : 'No Changes Allowed' }}</span>
                        </div>
                        <b-button
                            v-if="!isReadOnly"
                            size="lg"
                            :variant="
                                shouldRecalculate ? 'primary' : 'minimal-primary'
                            "
                            @click="onCalculate"
                            >{{
                                shouldRecalculate ? "Re-Calculate" : "Calculate"
                            }}</b-button
                        >
                        <div class="d-flex gap-3 align-items-center">
                            <b-badge 
                                v-if="!isReadOnly && hasUnsavedChanges" 
                                variant="soft-danger" 
                                class="font-size-80 text-uppercase"
                            >
                                Unsaved Changes
                            </b-badge>
                        </div>
                        <!-- <b-button @click="checkAndShowErrors">Check Form</b-button> -->
                    </div>
                    <div class="d-flex align-items-center gap-2">
                        <div
                            v-if="hasAnyErrors && showErrors"
                            class="text-danger font-weight-600 mr-4 text-center"
                        >
                            <b-icon icon="exclamation-circle-fill"></b-icon>
                            Fix Errors
                            <div v-if="!allChildFormsValid" class="text-danger font-size-70">Invalid Components</div>

                        </div>
                        <b-badge
                            v-if="showSuccess"
                            variant="success"
                            class="text-uppercase font-size-100 mr-4"
                            >Saved</b-badge>
                        <CancelButton class="mr-0" @cancel="onCancel" />
                        <!-- <SaveButton v-if="hasAnyErrors && showErrors" :locked="isReadOnly || showSuccess" text="Save Progress & Close" @save="onSave({saveWithErrors: true})" />
                        <SaveButton v-else :locked="isReadOnly || showSuccess" text="Save and Close" @save="onSave" /> -->

                        <b-button 
                            v-if="isReplyMode && allowSaveInvalidReply && hasAnyErrors && showErrors" 
                            variant="secondary" 
                            size="lg" 
                            :disabled="isReadOnly || showSuccess" 
                            @click="onSave({saveWithErrors: true})"
                        >Save Progress & Close<br><span class="font-size-70">Form is Incomplete / Invalid</span></b-button>
                        <b-button 
                            v-else 
                            variant="primary" 
                            size="lg" 
                            :disabled="isReadOnly || showSuccess" 
                            @click="onSave"
                        >Save and Close</b-button>  <!-- the original save button -- but we show other ones in certain conditions -->
                    </div>
                </div>
            </div>
        </div>
        <div v-else key="stillLoadingButtonArea" class="">
            <!-- we still show cancel button, but that's it... otherwise it is confusing whats going on for the user -->
            <div class="smartform-footer">
                <div class="d-flex justify-content-end w-100">
                    <CancelButton class="mr-0" @cancel="onCancel" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { errorHandling, currentUserInfoMixin } from "../../js/custom-vue-mixins";
import { suppliosApi } from "../../js/api-service";
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import invoke from 'lodash/invoke';
import FormioForm from "../demo-workflow/FormioForm";
import LoadingContext from "../shared/LoadingContext";
import CancelButton from "../shared/buttons/CancelButton";
// import SaveButton from "../shared/buttons/SaveButton";
//import ItemAutocomplete from '../items/ItemAutocomplete.vue';
import ItemAutocomplete from '../items/ItemAutocompleteMultiselect.vue';
import FileSlingerListUploader from '@/components/s3_files/FileSlingerListUploader.vue';


/* class ChildSmartForm {  // OLD VERSION - worked with SmartFormComponent serializer - replace with LiveSmartForm below
    // used to show child forms OR replies
    // we use same one for both SETUP and REPLY mode so its consistent
    // these are injected into the FormioForm schema also... they expect at least { id, name }
    constructor({
        // set defaults here - make sure to use them because some things are not populated for internal/external use cases
        form,  // full form instance
        reply = null,  // full reply instance, if there is one
        isReplyMode,  // pass this in from vue component so we know what mode we're in
    }) {
        this.form = {...form};
        this.id = form.id;  // always use form ID as id... we'll always have form, not always reply - also needed in formio schema!
        this.name = this.form.name 
        this.formId = this.form.id;  // already have it as 'id' but just to be explicit
        this.isFormValid = this.form.is_setup_valid;  // might be null when vendor views
        this.formHasReplies = this.form.has_any_replies;  // might be null when vendor views
        // REPLY THINGS
        this.reply = reply ? {...reply} : null;  // might be null/undefined if no reply yet, or internal
        this.replyId = this.reply?.id ?? '';
        this.isSubmitted = this.reply?.is_submitted ?? false;
        this.isReplyValid = this.reply?.is_reply_valid; 
        // COMMON
        this.isReplyMode = isReplyMode;  // stash on here, just so we have it easily
        this.isValid = isReplyMode ? this.isReplyValid : this.isFormValid;
        this.validText = isReplyMode ? 'Complete' : 'Ready';
        this.invalidText = isReplyMode ? 'Incomplete' : 'Not Ready';
        this.showDelete = !isReplyMode;
        this.deleteButtonDisabled = isReplyMode ? this.isSubmitted : this.formHasReplies;
        this.bgClass = isReplyMode ? 'bg-warning-100' : 'bg-warning-100';
        // show delete?  - based on formHasReplies in setup, or is_submitted in reply?
    }
} */

class LiveSmartForm {
    // used to show child forms OR replies
    // we use same one for both SETUP and REPLY mode so its consistent
    // AUG 29 2022 - NEW VERSION - uses the same schema as the parent form - from LiveSmartFormSerializer on backend
    constructor({
        // set defaults here - make sure to use them because some things are not populated for internal/external use cases
        form,  // full form instance
        reply = null,  // full reply instance, if there is one
        is_read_only,  // backend pulls from reply.is_submitted / form.has_any_replies based on mode 
        is_valid,  // backend pulls from is_reply_valid / is_setup_valid based on mode we're in
        child_forms,  // any further nested children, same schema
        isReplyMode,  // pass this in from vue component so we know what mode we're in
        parentIsReadOnly,  // if the parent (the main one in vue component) is read only, this is true - passed in from vue component context
    }) {
        this.form = {...form};
        this.id = form.id;  // always use form ID as id... we'll always have form, not always reply - also needed in formio schema!
        this.name = this.form.name  // we want 'name' on top-level for various things (in schema of FormioForm, for display, etc.)
        this.formId = this.form.id;  // already have it as 'id' but just to be explicit
        //this.isFormValid = this.form.is_setup_valid;  // might be null when vendor views
        //this.formHasReplies = this.form.has_any_replies;  // might be null when vendor views
        // REPLY THINGS
        this.reply = reply ? {...reply} : null;  // might be null/undefined if no reply yet, or internal
        this.replyId = this.reply?.id ?? '';
        this.isSubmitted = this.reply?.is_submitted ?? false;  // we still have this in reply
        //this.isReplyValid = this.reply?.is_reply_valid; 
        // COMMON
        this.isReadOnly = is_read_only || parentIsReadOnly;  // if parent is read only, we must also make this read only!
        this.parentIsReadOnly = parentIsReadOnly;  // need to track self vs. parent separately... so we know if we can create a revision
        this.isReplyMode = isReplyMode;  // stash on here, just so we have it easily
        this.isValid = is_valid;  // isReplyMode ? this.isReplyValid : this.isFormValid;
        this.bgClass = this.isReplyMode ? 'bg-warning-50' : 'bg-warning-50';  // we'll show this as a vendor-input (yellow bg) for internal OR external for now

        // CHILD FORMS / CHILD REPLIES - same... we use LiveSmartForm as common interface on front and backends
        this.childForms = child_forms.map(cf => new LiveSmartForm({...cf, isReplyMode: this.isReplyMode, parentIsReadOnly: this.isReadOnly}));  // recursively build any grandchildren
        this.loadingEdits = false;  // show loading spinner on create/edit button

        // BUTTONS 
        // there are 3 different buttons:
        // delete -- for BOTH setup and reply modes
        //      setup - to delete a SmartForm if possible (if no replies yet) 
        //      reply - to delete a DRAFT reply
        // view/edit:
        //      edit existing -- for editing in setup mode, or editing reply in reply mode... only if allowed to edit, either existing draft, or create a new draft
        //      view existing -- for either mode, to VIEW (not edit) existing form/reply if its read-only
        // create:
        //      revise - new draft from existing -- only for reply mode, to make a NEW revision
        //      create new reply -- for starting a new reply, no exiting one -- combine with edit/revise?

        // REPLY MODE - backend also enforces these (most of them) but we enforce here too
        // multiple can be true at same time
        const parentAllowsModify = this.isReplyMode && !this.parentIsReadOnly;  // !this.isReadOnly;  // for convenience below
        this.canEditReply = parentAllowsModify && !!this.reply && !this.isSubmitted && !this.isReadOnly;  // if its a draft, can edit, as long as we already have a reply and not submitted, and itself is not otherwise read only
        this.canCreateRevisedReply = parentAllowsModify && !!this.reply && this.isSubmitted;  // if we have a reply, but its submitted already
        this.canCreateNewReply = parentAllowsModify && !this.reply;  // if no reply yet
        this.canViewReply = this.isReplyMode && !!this.reply;  // if we have a reply to view! read only or not

        // DELETE BUTTON
        this.showDeleteButton = this.isReplyMode ? (!!this.reply && !this.isReadOnly) : !this.isReadOnly;  // should work for both reply/setup -- if child is not read only, and parent is not read only
        // show delete?  - based on formHasReplies in setup, or is_submitted in reply?

        // EDIT/VIEW BUTTON - to VIEW or EDIT an existing reply in reply mode, or an exisitng form in setup mode
        this.showEditButton = this.isReplyMode ? !!this.reply : true;  // if in reply mode, then can view/edit as long as we have a reply -- and can always view/edit in setup mode
        this.editButtonText = this.isReadOnly ? 'View' : 'Edit Draft';
        this.editButtonVariant = 'gray-primary';
        this.editButtonIcon = this.isReadOnly ? '' : '';  // for either mode - no icon for 'View Existing' on purpose
        
        // CREATE BUTTON - only used in reply mode - to create new revision, or create first reply 
        this.showCreateButton = this.isReplyMode && (this.canCreateRevisedReply || this.canCreateNewReply);
        this.createButtonText = this.reply ? 'Revise' : 'Start';  
        this.createButtonVariant = this.reply ? 'gray-primary' : 'sharp-success';
        this.createButtonIcon = this.reply ? '' : 'play';  // no icon for revise, on purpose

        // BADGE for CHILD 
        if (this.isReplyMode) {
            this.badgeText = this.isSubmitted ? 'Submitted' : (this.isValid ? 'Draft' : 'Incomplete');
            this.badgeVariant = this.isSubmitted ? 'success' : (this.isValid ? 'primary' : 'danger');
        } else {
            this.badgeText = this.isValid ? 'Ready' : 'Not Ready';
            this.badgeVariant = this.isValid ? 'success' : 'danger';
        }
    }
}

// FORMIO ASSET URLS
// you were using the 'full' builds of version 4.14.7 through December 2025
// but seems we should be able to use the 'form.min.js' and 'form.min.css' versions... they don't have the builder bundled, but we don't need those and we save a lot of size that way
// this is important for loading assets from China, and slow connections
// NOTE: you SHOULD use the jsdelivr CDNs because they have dedicated CDNs for China
const FORMIO_JS_FULL_URL = 'https://cdn.jsdelivr.net/npm/formiojs@4.14.2/dist/formio.full.min.js';  // content-length: 399038 -- not much different than form.min.js (maybe has other stuff loaded lazily?)
const FORMIO_CSS_FULL_URL = 'https://cdn.jsdelivr.net/npm/formiojs@4.14.2/dist/formio.full.min.css';  // content-length: 16164
const FORMIO_JS_STD_URL = 'https://cdn.jsdelivr.net/npm/formiojs@4.14.2/dist/formio.form.min.js';  // content-length: 398569, resource size is 1,777 KB
const FORMIO_CSS_STD_URL = 'https://cdn.jsdelivr.net/npm/formiojs@4.14.2/dist/formio.form.min.css';  // content-length: 8332 -- NOT CORRECT?  sometimes 400s?
const USE_FULL_FORMIO_ASSETS = true;  // toggle this to switch between full and standard versions
const FORMIO_JS_URL = USE_FULL_FORMIO_ASSETS ? FORMIO_JS_FULL_URL : FORMIO_JS_STD_URL;
const FORMIO_CSS_URL = USE_FULL_FORMIO_ASSETS ? FORMIO_CSS_FULL_URL : FORMIO_CSS_STD_URL;

function hasFormioOnPage() {
    const hasFormio = window && window.Formio;
    const hasFormioUtils = window && window.FormioUtils;
    // ideally we would check that the CSS is also loaded, but that is harder... 
    if (!hasFormio) {
        console.warn('from hasFormioOnPage -- Formio JS not found on window!');
    }
    if (!hasFormioUtils) {
        console.warn('from hasFormioOnPage -- Formio Utils not found on window!');
    }
    return hasFormio && hasFormioUtils;
}


export default {
    name: 'SmartFormFormio',
    props: {
        // isShown: {
        //     type: Boolean,
        //     default: false,
        // },
        formId: {  // when working with a form in SETUP MODE, only pass in formId
            type: String,
            default: '',
        },
        replyId: {  // when working with a REPLY, only pass in replyId
            type: String,
            default: '',
        },
        readOnly: {  // NOTE - there is something in data called 'isReadOnly' - which is set based on API data
            type: Boolean,
            default: false,
        },
    },
    components: {
        FormioForm,
        LoadingContext,
        CancelButton,
        // SaveButton,
        ItemAutocomplete,
        FileSlingerListUploader,
    },
    mixins: [errorHandling, currentUserInfoMixin],
    data: function () {
        return {
            error: false,
            isLoadingForm: true,
            isLoadingCalculations: false,
            isAfterFirstChange: false, // set true once its loaded once
            isFormValid: false, // updated from formio-form 'is-valid' event
            showErrors: false, // set true if we call checkAndShowErrors and it fails
            showSuccess: false, // success message on final save
            trackCalculations: false, // set true when we're ready to start the calculationsDirty tracking
            calculationsDirty: false, // if we see this in 2 subsequent onChange events, we should promt for recalculate
            shouldRecalculate: false, // set true when data changes to prompt user to recalculate
            calculationsTimeout: null,
            calculationsTimeoutMilliseconds: 1000,
            schema: null,
            originalSchema: null,  // stashed original from API
            schemaHash: '',  // stringified schema, to force update on changes
            hasAnyInternalOnlyFields: false,  // set when we first get form from api, inspect schema, or add property to top-level liveForm
            replyObj: {}, // will ONLY have it in reply mode - the reply object from backend
            formObj: {},  // SmartForm instance - not the submission data 
            formData: {},  // the actual submission data
            isReadOnly: false, // based on API response, logic depends on whether we are in REPLY mode or SETUP mode
            initialSubmissionData: {},  // we feed this to submissionData prop of FormioForm component - but DO NOT USE IT AFTER THAT
            // I think there is a bug in the FormioForm component that mutates the submissionData object, but it isn't supposed to...
            // so... we'll cloneDeep the initial submission data, set it here, feed that to FormioForm prop, then forget about it
            // we can also use initialSubmissionData for checking for unsaved changes...
            lastSavedDataStringified: '',
            hasUnsavedChanges: false,  // update this with a debounced watcher, compare current ata to lastSavedDataStringified
            formOptions: {
                // options to pass to formio-form
                //alwaysDirty: false,  // have not found any benefit of setting alwaysDirty to true yet
                highlightErrors: true,
            },
            downloadEmptyFormUrl: '',
            //formName: "",
            //formInstructions: "",
            //formExtraClasses: "",  // likely a string, but we might change to array
            // loading assets
            formioCssId: 'formio-stylesheet',
            formioJsId: 'formio-js-script',
            isJsLoaded: false,
            isCssLoaded: false,
            assetsLoaded: false, // set true when all ASSETS are ready 
            assetsLoadingStartTs: null,  // timestamp when we started loading assets, for measuring load time
            jsLoadingTimeMs: null,  // duration (in ms) for JS to load
            cssLoadingTimeMs: null,  // duration (in ms) for CSS to load
            // CHILD FORMS 
            // we use same things whether we're in SETUP or REPLY mode 
            // use LiveSmartForm instances as common interface
            loadingChildForms: false,
            //openChildForm: {},  // set to the one you currently want open - full object instance
            openChildFormId: '',  // just the ID of the FORM instead... we can get replyId from that if/when needed
            childFormEditModalShowing: false,  // when viewing child, this controls modal open/close
            //allowChildForms: false,  // set from API data
            childFormStore: {},  // local store of child forms, keyed by ID - updated when child forms emit updates
            addChildData: {},  // before you show modal, set to {name, template_form} with empty values
            templateOptions: [],  // template options for addChild
            savingChild: false,  // loading for save operation
            // CHILD FORM REPLIES STUFF
            childFormReplyStore: {},  // local store of child form reply instances, keyed by replyId
            addChildModalShowing: false,  // show/hide modal

        };
    },
    computed: {
        isReplyMode() {
            // we can tell if its internal or external by whether we have formId or replyId
            // this changes the endpoints used, and other behaviors, so quite important
            return Boolean(this.replyId);
        },
        addChildOkDisabled() {
            return !!this.savingChild || !!this.isReplyMode || !this.addChildData?.name || !this.addChildData?.template_form;
        },
        childForms() {
            // we pull these out of the childFormStore into an array, use these in v-for in template 
            // make sure order is how you want it for display, if that's important
            return Object.values(this.childFormStore);
        },
        childFormReplies() {
            // only when in reply mode 
            return Object.values(this.childFormReplyStore);
        },
        showChildFormArea() {
            if (this.isReplyMode) {
                return !!this.childForms.length;
            }
            return !!this.formObj?.allow_child_forms;  // allowChildForms;  // you may separate allowing vs. can add new
        },
        allChildFormsValid() {
            // for reply mode or setup mode -- are they all valid
            return Object.values(this.childFormStore).every(cf => cf.isValid);
        },
        allowSaveInvalidReply() {
            // in reply mode, should we allow saving if the form is invalid?  this is based on a flag from backend
            return this.isReplyMode && this.formObj?.allow_save_invalid_reply;
        },
        hasAnyErrors() {
            // combine all error / invalid things here
            return !this.isFormValid || !this.allChildFormsValid;
        },
        childFormsDisplayName() {  // set by backend
            return this.formObj?.child_forms_display_name ?? 'Components';
        },
        childFormsDescription() {
            // set by backend, different for internal/external
            return (this.isReplyMode ? this.formObj?.child_forms_external_description : this.formObj?.child_forms_internal_description) ?? 'Fill out these component Quote Sheets.';
        },
        openChildReplyId() {
            // get it from openChildFormId and the childFormStore
            return this.openChildFormId ? this.childFormStore[this.openChildFormId].replyId : '';
        },
        showDownloadEmptyFormButton() {
            // should we show button to download the empty form?
            // we'll show it all the time for INTERNAL/SETUP mode
            // but NOT AFTER SUBMISSION (ie: not when isReadOnly) for reply mode - or else its confusing why the reply data is not there
            const replyModeChecks = !!this.downloadEmptyFormUrl && !(this.isReadOnly && this.isReplyMode);
            // June 2024 - added flag to control this in form _data
            return this.formObj?.show_download_excel_template && replyModeChecks;
        },
        showInternalOnlyNote() {
            // show the internal only note if we're in setup mode and the form has any internal_only fields
            return !this.isReplyMode && this.hasAnyInternalOnlyFields;
        },
        existingGeneralFiles() {
            return this.formObj?.general_files ?? [];
        },
        showGeneralFilesArea() {
            // in setup mode -- show the general files area if we have the flag set in backend
            // in reply mode -- show the general files area if we have any files already (no need to check the flag)
            return this.isReplyMode ? this.existingGeneralFiles.length : this.formObj?.show_general_files_setup;
        },
    },
    methods: {
        loadFormioCss() {
            // TODO: move this to formio-form - or just finally install formio as actual dependency...
            // CHECK IF ALREADY LOADED - this is recursive component so might be!
            const isLoaded = Boolean(document.getElementById(this.formioCssId));
            const onLoad = () => {
                console.log('HELLO FROM FORMIO CSS onLoad CALLBACK - CSS IS LOADED from this URL:', FORMIO_CSS_URL);
                this.isCssLoaded = true;
                this.cssLoadingTimeMs = Date.now() - this.assetsLoadingStartTs;
                this.checkAssetsLoaded();
            };
            if (isLoaded) {
                //console.log('CSS already loaded');
                onLoad();  // call callback immediately
            } else {
                // if not loaded yet, construct the element and pass it a callback for when it loads
                let formioCss = document.createElement('link');
                formioCss.rel = 'stylesheet';
                formioCss.type = 'text/css';
                formioCss.id = this.formioCssId;
                //formioCss.href = "https://cdn.jsdelivr.net/npm/formiojs@4.14.2/dist/formio.full.min.css";
                formioCss.href = FORMIO_CSS_URL;
                formioCss.addEventListener('load', onLoad, {once: true});  // specifying once=true in options will remove event listener after calling first time
                document.head.appendChild(formioCss);  // add it!
            }
        },
        loadFormioJs() {
            // TODO: move this to formio-form - or just finally install formio as actual dependency...
            // CHECK IF ALREADY LOADED - this is recursive component so might be!
            // also check if we already have it on the page from html template?  do that before calling this, maybe just by checking if we have Form.io globals on window?
            const isLoaded = Boolean(document.getElementById(this.formioJsId));
            const onLoad = () => {
                console.log('HELLO FROM FORMIO JS onLoad CALLBACK - JS IS LOADED from this URL:', FORMIO_JS_URL);
                this.isJsLoaded = true;
                this.jsLoadingTimeMs = Date.now() - this.assetsLoadingStartTs;
                this.checkAssetsLoaded();
            };
            if (isLoaded) {
                //console.log('JS already loaded');
                onLoad();  // call callback immediately
            } else {
                // if not loaded yet, construct the element and pass it a callback for when it loads
                let formioScript = document.createElement("script");
                formioScript.id = this.formioJsId;
                //formioScript.src = "https://cdn.jsdelivr.net/npm/formiojs@4.14.2/dist/formio.full.min.js";
                formioScript.src = FORMIO_JS_URL;
                formioScript.addEventListener('load', onLoad, {once: true});  // specifying once=true in options will remove event listener after calling first time
                document.head.appendChild(formioScript);
            }
        },
        reportAssetLoadingEvent() {
            // super defensive - so we don't inject more problems just trying to report load times
            const actionName = 'spl-smartform-formio-assets-loaded';
            console.log(`Reporting RUM action for ${actionName} with jsLoadingTimeMs:`, this.jsLoadingTimeMs, 'cssLoadingTimeMs:', this.cssLoadingTimeMs);
            if (window.DD_RUM) {
                try {
                    window.DD_RUM.addAction(actionName, {
                        'jsLoadingTimeMs': this.jsLoadingTimeMs,
                        'cssLoadingTimeMs': this.cssLoadingTimeMs,
                    });
                } catch (error) {
                    console.error(`Error reporting RUM action for ${actionName}:`, error);
                }
            }
        },
        checkAssetsLoaded() {
            // used in listener for when assets are loaded
            // NEW - check if we have Formio on page too, in case it was loaded some other way
            if (hasFormioOnPage()) {
                this.isJsLoaded = true;
                this.isCssLoaded = true;  // assume this to be true if we have Formio... not perfect but better than nothing
            }
            //console.log(`from checkAssetsLoaded isCssLoaded? ${this.isCssLoaded} isJsLoaded? ${this.isJsLoaded}`);
            if (this.isJsLoaded && this.isCssLoaded) {
                //console.log("Assets loaded - we can show the form");
                this.assetsLoaded = true;
                this.reportAssetLoadingEvent();
                // now that assets are loaded, we can show the form
                this.$nextTick(() => {
                    this.onShow();
                });  // wait for next tick just to be safe
                // this.onShow();
            } else {
                console.log('Still waiting on assets to load...');
            }
        },
        onShow() {
            this.getForm();
        },
        async getForm() {
            this.isLoadingForm = true;

            try {
                // NOTE - different endpoints depending on if we're doing form SETUP or form REPLY
                // but returned data schema is the SAME - via LiveSmartFormSerializer on backend
                const { data } = this.isReplyMode
                    ? await suppliosApi.smartFormReply.get_for_submission(
                          this.replyId
                      )
                    : await suppliosApi.smartForm.get_for_setup(this.formId);
                // we get instance of LiveSmartForm via backend LiveSmartFormSerializer -- our consistent interface for parent/child/form/reply (all of them)
                // we have helper method to update all local things from that, then another one to gather local things to emit to parent if/when needed
                this.setLocalDataFromLiveSmartForm(data);
                // for SETUP mode, the submission data is the setup data -- things that will become defaults when in reply mode
                // for REPLY mode, the submission data is the actual submission data for a reply
            } catch (error) {
                this.apiErrorHandler(error);
            }

            this.isLoadingForm = false;
            // do not update parent here... will certainly be invalid here, update parent when form updates validity
        },
        async getChildForms() {
            // make API call to get refreshed list of child_forms
            // we have endpoints for form and reply -- same name, use from different modes
            this.loadingChildForms = true;
            let childFormList;
            try {
                const resp = this.isReplyMode
                    ? await suppliosApi.smartFormReply.list_child_forms(this.replyId)
                    : await suppliosApi.smartForm.list_child_forms(this.formId);
                childFormList = resp.data;
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (childFormList) {
                this.updateLocalChildForms(childFormList);
            }
            // always recalculate too -- in case new child forms change calculations
            this.onCalculate();  // await it?  don't think we need to...
            this.loadingChildForms = false;
        },
        setLocalDataFromLiveSmartForm(liveSmartFormObj) {
            // helper to take data from backend LiveSmartFormSerializer and update all local things
            // split things out?  or just make one instance of LiveSmartForm and do all things from there???
            // for updating submission data, etc... makes sense to move things to top level
            const { form, schema, reply, submission, is_read_only, is_valid, child_forms, download_empty_form_url, has_any_internal_only_fields } = liveSmartFormObj;   
            // stash the form, reply, other easy things
            this.formObj = {...form};  
            this.replyObj = reply ? {...reply} : {}; // should only have this if isReplyMode=true
            this.isReadOnly = is_read_only || this.readOnly;  // NOV 9 2022 - added prop readOnly to force read-only from parent (based on TU role, etc.)
            this.isFormValid = is_valid;  // this will likely get overwritten right away by local validation logic, which is OK
            this.downloadEmptyFormUrl = download_empty_form_url ?? '';  // URL for downloading Excel version of this  (empty = no reply data)
            // build the schema - add some 'customData' to the schema to pass down extra stuff
            this.originalSchema = cloneDeep(schema);
            this.rebuildSchema(schema);
            // set the submission data 
            const submissionData = submission || {};  // we have this for BOTH setup and reply modes 
            this.initialSubmissionData = cloneDeep(submissionData);
            this.lastSavedDataStringified = JSON.stringify(this.initialSubmissionData); // stash to check for unsaved changes
            this.formData = submissionData;
            this.hasAnyInternalOnlyFields = has_any_internal_only_fields;
            // those are the things we have to do every time... 
            
            // IF WE HAVE CHILD FORMS:
            // child_forms from backend now have the same schema as this -- all from LiveSmartFormSerializer
            this.updateLocalChildForms(child_forms);
        },
        updateLocalChildForms(childFormsArray) {
            // use this to update the local childFormStore - from original API call, or refresh of child_forms list
            // we keep child forms in 'childFormStore' -- same whether we are in SETUP or REPLY modes -- all keyed by form ID
            // we'll make instances of our class for holding form + reply info
            // do it by iteratring over the child FORMs, not replies... might not have replies yet
            const childForms = childFormsArray ? [...childFormsArray] : [];  
            const childFormStore = {};  // keyed by FORM ID
            childForms.forEach(cform => {  // do the loop whether we are in SETUP or REPLY
                // if in SETUP mode, reply will be null/undefined, which is OK
                const cfInst = new LiveSmartForm({...cform, isReplyMode: this.isReplyMode, parentIsReadOnly: this.isReadOnly});
                childFormStore[cfInst.id] = cfInst;  // stash in store by the FORM ID
            });
            this.childFormStore = childFormStore;  // set on component data -- doing it this way so we don't have to use this.$set each time we add one
            // must also rebuild the schema anytime we do this, since we add childForms stuff to the schema locally
            this.rebuildSchema();  // do not need to pass in existing schema... will do it itself
        },
        getLiveSmartFormInstFromLocalData() {
            // reconstruct the LiveSmartForm instance from local data, for passing up to parent if needed
            const data = {
                form: this.formObj,
                reply: this.replyObj,
                is_read_only: this.isReadOnly,
                is_valid: this.isFormValid,
                child_forms: Object.values(this.childFormStore),
                schema: this.originalSchema,
                submission: this.formData,
                isReplyMode: this.isReplyMode,
                parentIsReadOnly: this.isReadOnly,
            };
            const dataClone = cloneDeep(data);  // pass up a clone, just in case we mutate things further here
            return new LiveSmartForm(dataClone);
        },
        cancelCalculationsTimeout() {  // no longer needed?
            clearTimeout(this.calculationsTimeout);
            this.calculationsTimeout = null;
        },
        async onChange(data) {
            // console.log('HELLO FROM onChange - formName:', this.formObj.name);
            // console.log('from ANY onChange', data);
            // const stringifiedData = JSON.stringify(data);
            // const stringifiedFormData = JSON.stringify(this.formData);
            // console.log('stringified data', stringifiedData);
            // console.log('stringified formData', stringifiedFormData);
            // console.log('same?', (stringifiedData === stringifiedFormData));
            // console.log('object values join length', Object.values(data).join("").length);
            const hasData = Object.values(data).join("").length > 0;  // any data at all?  what if we deleted all things, and we wanted to remove things?  keys should still be there.... I think
            if (hasData) {
                // doing it like this so we only have to stringify things once... because we use it to look for ANY changes, and to check for unsaved changes
                const newDataStringified = JSON.stringify(data);
                if (JSON.stringify(this.formData) !== newDataStringified) {
                    console.log('from onChange - has a real change');
                    //console.log('will update this.formData with new data');
                    this.formData = cloneDeep(data);  // cloneDeep is VERY IMPORTANT HERE - or else nested component data will get mutated unexpectedly in the form
                    // also need to make sure we set this BEFORE doing onCalculate()  
                    if (!this.isAfterFirstChange) {
                        // for calling onCalculate after the data is initially loaded
                        // Aug 29 2022 - also, this will be our 'baseline' for tracking changes - this only happens once
                        this.lastSavedDataStringified = newDataStringified;
                        this.isAfterFirstChange = true; // set true right away, otherwise we'll get double calls to this while first onCalculate is in process
                        console.log('FIRST CHANGE - calling onCalculate first time');
                        await this.onCalculate();
                        this.trackCalculations = true; // start this only after the first onCalculate
                        this.calculationsDirty = true;  // need this here or else the first change doesn't trigger need to recalculate
                    } else if (this.trackCalculations) {
                        // for tracking changes and prompting user to re-calculate
                        if (this.calculationsDirty) {
                            console.log('calculationsDirty is true, SHOULD recalculate');
                            this.shouldRecalculate = true;
                            this.resetCalculatedInputs();
                        } else {
                            console.log('calculationsDirty is FALSE, next time...');
                            this.calculationsDirty = true;
                        }
                    }
                    // this.updateHasUnsavedChanges();  // the debounced version... actually, no need anymore
                    // this.cancelCalculationsTimeout();
                    // this.calculationsTimeout = setTimeout(() => {
                    //     this.onCalculate();
                    // }, this.calculationsTimeoutMilliseconds);
                }
                this.hasUnsavedChanges = newDataStringified != this.lastSavedDataStringified;
            } else {
                // if no data from form (empty!) still need to check if unsavedChanges
                this.hasUnsavedChanges = !!this.lastSavedDataStringified;  // did we have ANYTHING before?
            }
        },
        _updateHasUnsavedChanges() {  // MAYBE NOT NEEDED ANYMORE
            // make DEBOUNCED version of this in created() and use that instead of this!
            // stringify data, compare, update local value
            // because of how formio works, this is NOT a great way to do this... if user enters text, then deletes it, stringified version will look different.
            // whatevs... OK for now.
            this.hasUnsavedChanges = JSON.stringify(this.formData) != this.lastSavedDataStringified;
        },
        onBlur() {
            // no data passed in from formio-form component (yet)
            // still not a good experience, even on @blur...
            // it also screws up the edit grid if open and update happens
            /* this.cancelCalculationsTimeout();
            this.calculationsTimeout = setTimeout(() => {
                this.onCalculate();
            }, this.calculationsTimeoutMilliseconds); */
        },
        onIsValid(isValid) {
            // formio-form emits 'is-valid' events - this handles
            // payload is just boolean of validity
            // also have method for checking validity and showing all errors in red
            // TODO - standardize on a single way of knowing if we have errors... this or calling customUpdateValidity() on the form?
            this.isFormValid = isValid;
            // we DO NOT updateParent on each validity change... only when we SAVE changes
        },
        checkAndShowErrors() {
            const formRef = this.$refs.form;  // won't have it yet if there is a problem loading formio assets (Dec 2025)
            if (!formRef) {
                console.error('in SmartFormFormio component we called checkAndShowErrors - but no form ref yet!  maybe assets did not load?');
                return false;  // not valid if we can't check it, right?  added Dec 2025
            }
            const isFormValid = formRef.customUpdateValidity({
                dirty: true,
                silent: false,  // NEW June 22 2024
            });
            //console.log("result of checkAndShowErrors:", isValid);
            return isFormValid && this.allChildFormsValid;
        },
        async onCalculate() {
            console.log('hello from onCalculate');
            
            // March 20 2025 - we were still calling this even if the form is read-only, but likely not needed since we should have gotten the full submission payload already (including calculated fields)
            // TODO - make sure this is safe to abort here... might break other things!  child forms OK?
            // TOOD - maybe abort earlier?
            if (this.isReadOnly) {
                // TODO - do we need to set calculationsDirty or shouldRecalculate or clear calc timeout?
                console.log('inside onCalculate - this.isReadOnly is true - not making the call to calculate_values');
                return;
            }
            this.isLoadingCalculations = true;

            try {
                const result = this.isReplyMode
                    ? await suppliosApi.smartFormReply.calculate_values(
                          this.replyId,
                          this.formData  // this.submissionData
                      )
                    : await suppliosApi.smartForm.calculate_values(
                          this.formId,
                          this.formData  // this.submissionData
                      )
                console.log('data from onCalculate to setSubmission with', result.data);
                // we might not have form yet when calling onCalculate... make sure it fails gracefully
                const formComp = this.$refs.form;
                if (formComp) {
                    formComp.setSubmission(result.data);
                }
                // OLD - caused console errors, but no UI error - this.$refs.form?.setSubmission(result.data);
                this.calculationsDirty = false;
                this.shouldRecalculate = false;
            } catch (err) {
                this.apiErrorHandler(err);
            }
            //console.log('Done with onCalculate');
            this.isLoadingCalculations = false;
            this.cancelCalculationsTimeout();
        },
        resetCalculatedInputs() {
            // get the actual formio instance from formio-form component
            // then use formio API to loop through all components, find the calculated ones using the custom property we set
            // and call resetValue() on each
            const formioInst = this.$refs.form?.form;
            if (!formioInst) return;
            formioInst.everyComponent((comp) => {
                const isCalculated =
                    comp.schema.properties?.is_calculated || false;
                if (isCalculated) {
                    comp.resetValue();
                }
            });
        },
        async onCancel() {
            // check for unsaved changes - if have any, show warning
            if (!this.isReadOnly && this.hasUnsavedChanges) {
                const canClose = await this.canClose();
                if (canClose) {
                    this.requestClose();
                } else {
                    // do nothing... the cancel is just a no-op
                }
            } else {
                // no unsaved changes?  then proceed normally
                this.requestClose();
            }
        },
        updateParent(updatedLiveSmartFormInst) {
            // emit top-level data to parent in case this is a nested form
            // payload to emit?  instance of our LiveSmartForm -- consistent for either mode 
            // either pass it in after saving it and making new instance from response data
            // or construct it from local data
            // we only call this and updateParent when we PERSIST our changes
            // therefore the original data in parent does not get overwritten until we SAVE
            const liveFormInst = updatedLiveSmartFormInst ?? this.getLiveSmartFormInstFromLocalData();
            this.$emit('update', liveFormInst);
        },
        handleChildUpdate(childLiveSmartForm) {
            // child forms will emit 'update' events (see updateParent above!) when it SAVES its data
            // this handles those events -- update our childFormStore
            // no need to pass up to any parent of this one... each should manage their own children
            // but if you consider child validation for this one's validation (likely) then you should call methods to check/update validity of this one
            // then maybe update THIS one's parent if needed... so may pass on up, but indirectly (after checking each ones own validation), not directly
            console.log('handleChildUpdate', childLiveSmartForm);
            //this.addOrUpdateChildForm(childLiveSmartForm);  // handle it all there....
            // now just reloading the list 
            this.getChildForms();  // await it?
        },
        async onSave({saveWithErrors = false} = {}) {
            const isValid = this.checkAndShowErrors();
            const canSaveInvalid = this.allowSaveInvalidReply && saveWithErrors;
            console.log("Done with checkAndShowErrors");
            if (!isValid && !canSaveInvalid) {
                this.showErrors = true;
                console.log("NOT VALID");
                return;
            } else if (!isValid && canSaveInvalid) {
                console.log("NOT VALID but saveWithErrors is true, so continuing with save...");
            } else {
                this.showErrors = false;
                console.log("IS VALID");
            }
            console.log("continuing on with save");
            let updatedLiveSmartFormInst;
            if (this.isReplyMode) {
                try {
                    const resp = await suppliosApi.smartFormReply.save_reply_data(this.replyId, this.formData);
                    // need to update local stuff to set new validation status before updateParent
                    console.log('response from save_reply_data', resp);
                    //this.replyObj = Object.assign({}, resp.data);  // do this before updateParent!
                    updatedLiveSmartFormInst = new LiveSmartForm({...resp.data, isReplyMode: this.isReplyMode, parentIsReadOnly: this.isReadOnly});
                } catch (err) {
                    // if submission has errors, response data will have dict (obj) of error messages, keyed by field ID
                    const errData = err.response.data;
                    const defaultMsg = 'There was a problem saving your submission';
                    const errList = errData ? Object.values(errData) : [defaultMsg];
                    let errMsg = (!!errList && Array.isArray(errList) && errList.length) ? errList.join('\n') : defaultMsg;
                    this.$toast.error(errMsg, {
                        timeout: false,
                    });
                    //this.apiErrorHandler(err);
                }
            } else {
                // for SETUP mode, we send more things along
                const postData = {
                    name: this.formObj.name,
                    instructions: this.formObj.instructions,
                    linked_products: this.formObj.linked_products,  // may have new ones to get created
                    defaults: this.formData 
                };
                try {
                    const resp = await suppliosApi.smartForm.save_form_setup(this.formId, postData);
                    //this.formObj = Object.assign({}, resp.data);
                    // this now returns same schema as all other places... LiveSmartForm
                    updatedLiveSmartFormInst = new LiveSmartForm({...resp.data, isReplyMode: this.isReplyMode, parentIsReadOnly: this.isReadOnly});
                } catch (err) {
                    this.apiErrorHandler(err);
                }
            }
            // we were successful?  if so, we'll have updatedLiveSmartFormInst
            if (updatedLiveSmartFormInst) {
                this.updateParent(updatedLiveSmartFormInst);
                this.onSuccess();
            }
        },
        onSuccess() {
            // this.updateParent();  // moved to after save API call directly
            // clear any errors, showSuccess, onHide with timeout
            this.isFormValid = false;
            // this.showErrors = false;  // we do this in requestClose instead -- just for better UI, but could change
            this.showSuccess = true;
            this.hasUnsavedChanges = false;
            // reset hasUnsaved
            setTimeout(() => {
                this.requestClose();  // also call reset()/hide() ???  or just let parent destroy the component?
                //this.onHide();
            }, 1500);
        },
        reset() {
            // likely not needed since we'll just call requestClose() to have parent close us, and we get destroyed
            this.onHide();
        },
        onHide() {
            // this really should be called reset() 
            // but no longer needed since we moved the modal out of here
            // this form can be inside a modal, or a collapse, or something else
            // use this.requestClose() instead, then no need to call this because everything just gets destroyed
            // but we'll keep this around to call from reset(), if needed
            // this.assetsLoaded = false;  // shouldn't need this unless its completely destroyed
            // RESET EVERYTHING
            this.isAfterFirstChange = false;
            this.isFormValid = false;
            this.showErrors = false;
            this.showSuccess = false;
            this.hasUnsavedChanges = false;
            this.trackCalculations = false;
            this.calculationsDirty = false;
            this.shouldRecalculate = false;
            this.schema = null;
            this.formObj = {};
            this.replyObj = {};
            this.formData = {};
            this.initialSubmissionData = {};
            this.isReadOnly = false;
            //this.formName = "";
            //this.formInstructions = "";
            this.$emit("hide");
        },
        /* onHidden() {
            // parent needs this to be compatible with others
            this.$emit("hidden");
        }, */
        async canClose() {
            // CALL FROM PARENT OR FROM OTHER LOCAL METHODS
            // call this to check if we have unsaved changes, if we do, show message/confirm
            // return a PROMISE - resolves true if OK to close, false if NOT
            if (!this.isReadOnly && this.hasUnsavedChanges) {
                // we make the message a little ambiguous, since our unsaved change detection is not perfect... will say we have unsaved changes even when we don't, especially for never-been answered forms
                return await this.$bvModal.msgBoxConfirm('Are you sure you want to close this?  You will lose any unsaved changes.', {
                        title: "Close?",
                        centered: true,
                        size: 'sm',
                    }).then((isConfirmed) => isConfirmed);   // this returns true if user clicks "OK", false if not - chaining the isConfirmed just for clarity....
            } else {
                return true;
            }
        },
        requestClose() {
            // emit event to parent to ask it to close me
            // parent is likely a modal, or collapse in a nested SmartForm structure
            // only call this when you're fully ready to close... any unsaved changes will be lost
            this.showErrors = false;
            this.$emit("request-close");
        },
        /* async close() {
            // call FROM PARENT - return promise? doesn't really work... because the component will disappear before this is done...
            // so just use canClose() instead?
        } */
        /////////////////////////////////////////////////////////////
        // FORM BUILDER - WORKING WITH NESTED FORMS / Child Forms
        // NOTE - if you only have ONE child form, show that as 'integrated' into the rest of form?  does that make sense all the time???
        // MAKE SOMETHING THAT CHECKS ASSEMBLY VS. CHILD FORMS TO MAKE SURE THERE ARE NO EXTRAS?
        // MODAL VERSION OF SHOWING CHILD FORMS
        // NOTE - next time you make changes to this, just separate out child forms as their own component, doing their own data fetching... especially for REPLY MODE
        //      - managing updates of child form replies here is a mess.... and you don't need the data locally... just on the server
        async handleChildFormEdit(childFormInst) {
            // open the modal based on passed child formo obj
            // this is called in SETUP AND REPLY modes 
            // used for VIEWING or EDITING existing REPLIES or existing FORMS (in setup mode)
            // you have separate method for creating NEW revisions of replies, or a first reply 
            // we will figure out what mode we're in, figure out what is allowed, get/create the form/reply, then set it on this.openChildFormId
            if (this.childFormEditModalShowing) {
                console.log(`CHILD FORM MODAL IS ALREADY SHOWING - IGNORING CALL TO OPEN IT FOR CHILD FORM ID ${childFormInst.id}`);
                return;
            }
            if (this.isReplyMode) {
                // check things we set on the LiveSmartForm instance to see what's allowed
                const canEditOrView = childFormInst.canEditReply || childFormInst.canViewReply;
                if (!canEditOrView) {
                    return;  // must be able to do one of those to proceed with this in reply mode
                }
            }
            //this.openChildForm = Object.assign({}, childFormInst);  // set it as the one shown in modal
            this.openChildFormId = childFormInst.id;
            this.childFormEditModalShowing = true;  // and open the modal
        },
        async handleChildFormReplyCreate(childFormInst) {
            // for CREATING new revisions of replies, or first reply
            if (this.childFormEditModalShowing) {
                console.log(`CHILD FORM MODAL IS ALREADY SHOWING - IGNORING CALL TO OPEN IT FOR CHILD FORM ID ${childFormInst.id}`);
                return;
            }
            const canCreate = childFormInst.canCreateRevisedReply || childFormInst.canCreateNewReply;
            if (!canCreate) {
                return;  // must be able to do one of those
            }
            // Start loader.... in case we need to do a create, might take a bit.
            childFormInst.loadingEdits = true;
            // call API to get new draft, then update our local store of child forms 
            const formId = childFormInst.formId;
            let newFormInst;
            try {
                const resp = await suppliosApi.smartForm.get_or_create_draft_reply(formId);  // this will create a new draft, OR create a new reply
                newFormInst = new LiveSmartForm({...resp.data, isReplyMode: this.isReplyMode, parentIsReadOnly: this.isReadOnly});
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (newFormInst) {
                //this.addOrUpdateChildForm(newFormInst);  // adds to, or replaces the object in our childFormStore
                // AWAIT THIS - so we have new list
                await this.getChildForms();
                // then set it as the one to show in modal, and open modal
                //this.openChildForm = Object.assign({}, newFormInst);  
                this.openChildFormId = newFormInst.id;
                this.childFormEditModalShowing = true;  
            }
            // turn off loader if we still have it around
            if (childFormInst) {
                childFormInst.loadingEdits = false;  // old childFormInst might be gone... might have been replaced by new one? don't need this?
            }
        },
        closeChildFormEditModal() {
            // close it -- reset things on @hidden
            this.childFormEditModalShowing = false;
        },
        resetChildFormEditModal() {
            // called from @hidden of the edit modal
            //this.openChildForm = {};
            this.openChildFormId = '';
            // also, trigger the need for recalculation / reset current calculations
            this.shouldRecalculate = true;
            // instead of just resetting calculations, we'll run them 
            this.onCalculate();
        },
        async showAddChildModal() {
            this.addChildData = {
                name: '',
                template_form: '',  // ID of the selected template
            };  // set this first, THEN open modal
            await this.getTemplateOptions();
            this.addChildModalShowing = true;
        },
        closeAddChildModal() {
            // close it, then let @hidden handler reset things
            this.addChildModalShowing = false;
        },
        resetAddChild() {
            // call when modal is totally hidden, reset things for adding
            // since its called from modal @hidden (to make sure it happens if OK or cancel is clicked), you don't need to call it elsewhere
            this.addChildData = {};
            this.templateOptions = [];  // do we need to reset it???
            // trigger need for recalc
            // instead of just resetting calculations, we'll run them 
            this.onCalculate();
        },
        async getTemplateOptions() {
            try {
                const resp = await suppliosApi.smartForm.get_possible_child_form_templates(this.formId);
                this.templateOptions = resp.data;
            } catch (error) {
                this.apiErrorHandler(error);
            }
        },
        addOrUpdateChildForm(childLiveSmartForm) {
            // add class instances of LiveSmartForm to our 'childFormStore' - both modes do this
            // child SHOULD make a new instance of self with updated data and pass that up.... that should be what we get here
            console.log('childLiveSmartForm', childLiveSmartForm);
            // add or update a childForm object in our childFormStore
            //const oldChildForm = this.childFormStore[childFormObj.id] ?? {};
            // REPLACE THIS WITH REFETCH OF getChildForms
            const newChildForm = cloneDeep(childLiveSmartForm);  // clone it just to be safe... will not happen often
            this.$set(this.childFormStore, childLiveSmartForm.id, newChildForm);  
        },
        async addChild() {
            // for adding childForms in SETUP mode - you have separeate one for creating replies in reply mode
            // check if things are valid first - keyup.enter might call this if not valid
            if (this.addChildOkDisabled) {
                return;
            }
            // this actually makes the POST
            this.savingChild = true;
            const childData = {...this.addChildData};  // shallow should be OK
            try {
                const resp = await suppliosApi.smartForm.create_child_form(this.formId, childData);
                const childFormData = resp.data;
                // must make a new instance of LiveSmartForm - this is only for SETUP MODE, so no reply
                // Aug 29 2022 - now this returns same schema as everywhere else... from LiveSmartFormSerializer
                const newChildFormInst = new LiveSmartForm({...childFormData, isReplyMode: this.isReplyMode, parentIsReadOnly: this.isReadOnly});
                await this.getChildForms();  // add await here to avoid calling onCalculate without the form rebuilt yet...
                //this.addOrUpdateChildForm(newChildFormInst);
                //this.rebuildSchema(this.schema);
                console.log('ADDED CHILD', newChildFormInst);
            } catch (error) {
                this.apiErrorHandler(error);
            }
            this.closeAddChildModal();
            this.savingChild = false;
        },
        async deleteChildForm(childFormObj) {
            // MUST HANDLE SETUP OR REPLY MODE
            // in SETUP mode, we delete the child form itself
            // in REPLY mode, we delete the reply 
            const confirmOk = await this.$bvModal.msgBoxConfirm('Are you sure you want to delete this?', {
                centered: true, 
                buttonSize: 'sm',
                size: 'sm',
                title: 'Please Confirm',
                okTitle: 'Yes, Delete',
                cancelTitle: 'No, Cancel',
                okVariant: 'danger',
            });
            if (!confirmOk) {
                return;  // bail if user said no/cancel
            }
            let deleteId = this.isReplyMode ? childFormObj.replyId : childFormObj.formId;
            let endpoint = this.isReplyMode ? suppliosApi.smartFormReply : suppliosApi.smartForm;
            try {
                const resp = await invoke(endpoint, 'destroy', deleteId);  // suppliosApi.smartForm.destroy(deleteId);
                console.log('destroy response', resp);
                // no meaningful response data  
                // remove it from childFormStore - you don't have a refresh list method...
            } catch (error) {
                deleteId = '';
                this.apiErrorHandler(error);
            }
            // NEW - we'll just refetch the list of child forms
            await this.getChildForms();
            /* if (deleteId) {
                // if we deleted... remove from store
                // deleteId will be replyId or formId depending on mode
                // but in store, we always key by formId - in both modes
                // TODO - need to refetch... because we might have deleted a draft reply, but still have a submitted reply to view
                const newChildFormStore = {};
                Object.values(this.childFormStore).forEach(cf => {
                    const wasDeleted = this.isReplyMode ? cf.replyId === deleteId : cf.formId === deleteId;
                    if (!wasDeleted) {
                        newChildFormStore[cf.id] = cf;
                    }
                });
                this.childFormStore = newChildFormStore;
            } */
        },
        rebuildSchema(rawSchema) {
            // pass in rawSchema, enhance with latest things and return it
            // can also not pass anything in, then we'll get current schema and use that instead
            // as of Aug 29 2022, we really only need array of {id, name} in customData.child_forms
            // also, we grab current this.formData, then set it back as initialSubmissionData on FormioForm after rebuilding schema
            //      why?  because we completely destroy and rebuild that component... maybe there is better way once you have cleaner FormioForm component
            const previousSubmissionData = cloneDeep(this.formData);
            const childFormArray = Object.values(this.childFormStore);
            const dataForSchema = {
                child_forms: cloneDeep(childFormArray),
            };
            const formioSchema = rawSchema ? cloneDeep(rawSchema) : cloneDeep(this.schema);
            // now add some 'customData' to the schema to pass down extra stuff
            formioSchema['customData'] = dataForSchema;
            // now we destroy and rebuild the form -- passing in the new schema, and the snapshot of submission data we just grabbed
            this.schemaHash = '';  // we hide the form when schemaHash is empty value
            // now update things...
            this.schema = formioSchema;
            this.initialSubmissionData = previousSubmissionData;
            // and now set a new truthy schemaHash to show form again
            this.schemaHash = JSON.stringify(formioSchema);
        },
    },
    created() {
        this.updateHasUnsavedChanges = debounce(this._updateHasUnsavedChanges, 500);
    },
    mounted() {
        //this.loadFormScript();
        this.assetsLoadingStartTs = Date.now();
        // check if already on page first!  if not, THEN we load them
        const alreadyLoaded = hasFormioOnPage();
        if (!alreadyLoaded) {
            console.log('From mounted hook of SmartFormFormio: Formio assets NOT already loaded on page - loading them now from the component');
            this.loadFormioCss();
            this.loadFormioJs();
        } else {
            console.log('From mounted hook of SmartFormFormio: Formio assets already loaded on page - skipping load, getting form now');
            this.checkAssetsLoaded();  // this will set assetsLoaded = true right away, then trigger the next things
        }
    },
};
</script>

<style lang="sass">

.smartform-formio-outer

    $internal-only-text-color: var(--info-800)
    $internal-only-bg-color: var(--info-100)
    $external-bg-color: var(--warning-50)
    $emphasis-item-text-color: var(--primary-700)
    $emphasis-item-border-color: var(--primary)
    $emphasis-item-bg-color: var(--primary-50)

    .internal-only-note 
        color: $internal-only-text-color 
        background-color: $internal-only-bg-color 
        padding: 0.25rem

    .internal-only.smartform-input:not(.formio-error-wrapper):not(.formio-hidden)
        background-color: $internal-only-bg-color 
        label, .input-group-prepend .input-group-text, .internal-only-note
            color: $internal-only-text-color 

    .external.smartform-input:not(.formio-error-wrapper):not(.formio-hidden)  // formio-error-wrapper highlights with red bg, should not conflict with that
        background-color: $external-bg-color 
        padding: 0.25rem   // do we need this padding?  we have separate padding rules in grid below... make sure they work together
        // but we actually do not want this padding inside a datagrid - remove it next 

    table.datagrid-table 
        //.external.smartform-input:not(.formio-error-wrapper):not(.formio-hidden) 
        //    padding: 0  // remove this if adding padding to components in table down below

    .vendor-input-section:not(.formio-hidden)
        background-color: $external-bg-color 

    .emphasis-item.smartform-input:not(.formio-error-wrapper):not(.formio-hidden),  // emphasis-item is the old class name, replaced by emphasis
    .emphasis.smartform-input:not(.formio-error-wrapper):not(.formio-hidden)
        //border-top: 1px solid $emphasis-item-border-color
        //border-bottom: 1px solid $emphasis-item-border-color
        border: 1px solid $emphasis-item-border-color
        background-color: $emphasis-item-bg-color
        .field-wrapper
            label
                font-weight: 700
                color: $emphasis-item-text-color
            .field-content
                input 
                    font-weight: 700
                    font-size: 120%

    table.datagrid-table,
    table.datagrid-table.table-bordered 
        border: 0 !important  // formio automatically adds table-bordered
        border-image-width: 0
        color: var(--gray-800)
        font-weight: 400
        //text-transform: uppercase
        font-size: 12px
        th
            font-size: 0.8rem
            font-weight: 500  // normally 700 per our top-level css 
        th, td 
            border: 0


    &.spl-formio-tight
        .form-group
            margin-bottom: 0.3rem
            // adding padding so we can add backgrounds nicely around all inputs for context communication
            // .field-wrapper (inside .form-group) already has top/bottom padding 
            border-radius: 0.25rem
            .field-wrapper 
                padding-left: 0.25rem
                padding-right: 0.25rem
        .card
            .card-header
                //padding-top: 0.25rem
                //padding-bottom: 0.25rem
            .card-body
                padding-top: 0.25rem
                padding-bottom: 0.25rem

        .field-wrapper > .field-label 
            display: flex 
            align-items: center
            &.field-label--right 
                justify-content: flex-end

        .formio-component > .form-text   // description text under each field 
            font-size: 0.75rem
            padding-left: 0.25rem  // give it same L/R padding as .field_wrapper
            padding-right: 0.25rem  

    .smartform-child-form-toggle
        &.not-collapsed > .toggle-inner > .badge-wrapper > .badge
            display: none

    .formio-component-number  // .smartform-fieldtype-money_fixed_currency
        input.form-control 
            text-align: right

    .formio-component-datagrid  // MORE STYLING AT THE BOTTOM FOR smartform-reply-mode / smartform-setup-mode
        .table 
            th
                padding: 0.35rem 0.25rem  // should match padding on the components in td -- td will have padding: 0
            td 
                padding: 0  // remove padding on td since those are not styled, no bg color -- add padding to the components inside the td - but still need padding on th since those don not have components inside 
                .smartform-input:not(.formio-error-wrapper):not(.formio-hidden) 
                    padding: 0.35rem 0.25rem  // giving a bit more top/bottom to make any special backgrounds show up better
                    margin-bottom: 0  // was 0.3rem in normal components, not in table
                .formio-component-number
                    label.field-required.control-label--hidden  // the red asterisk in required cell input 
                        left: 5px  // move it left, since we align input text right 
                        right: unset
                .formio-component-select 
                    .control-label--hidden.field-required
                        left: 5px 
                        right: unset 
                button.formio-button-remove-row
                    margin-top: 0.35rem

                .formio-error-wrapper 
                    padding: 0.5rem  // normal padding for error wrapper is 1rem, we make it smaller in the tables - maybe not just in tables???

    .formio-component-editgrid 
        &.spl-noheader 
                ul.editgrid-listgroup > li.list-group-header
                    display: none

    .formio-component-tabs > div.card > div.card-header .nav-link  
        font-weight: 700
        font-size: 1.2em 

    .smartform-section-heading 
        margin-top: 2rem
        margin-bottom: 1rem
        h1, h2, h3, h4, h5, h6 
            padding: 0.25rem 0.5rem 
            background-color: var(--primary-100)
            border-radius: 0.25rem

    .smartform-section-subheading 
        // margin-top: 2rem
        margin-bottom: 0.25rem
        h1, h2, h3, h4, h5, h6 
            padding: 0.25rem 0.5rem 
            background-color: var(--gray-150)
            border-radius: 0.25rem

    &.smartform-setup-mode 
        .formio-component-datagrid.smartform-grid-rowsource-setup.external 
            background-color: transparent  // remove yellow background when setting the rows to be used as rowsource
            // yellow background will still appear when vendor sees it, but not when setting it up


    // MITCHELLs ORIGINAL CSS - must add special css class to form._data['extra_classes']
    .quote-worksheet-v1

        $internal-only-text-color: var(--info-600)

        .internal-only-note 
            color: $internal-only-text-color 

        fieldset
            padding: 0.25rem 1rem
            legend
                margin: 0 0 .5rem
                font-size: 1.2rem
                font-weight: bold
                border-bottom: 1px solid rgba(black, .3)
                text-align: center

        .wizard-page
            display: flex
            flex-wrap: wrap

            .formio-component-fieldset
                width: 33.3%

            .formio-component-vendor_assembly_cost_total
                margin-top: 2rem
                margin-bottom: 1rem
                .input-group-text 
                    font-weight: 700

            .formio-component-assembly_information
                width: 100%
                .form-group
                    width: calc(33.3% - 1.5rem)
                .formio-component-fieldset
                    width: 100%
                    fieldset
                        padding: 0
                    .card
                        padding: 0 0.5rem
                        margin-bottom: 0 !important
                        > div
                            display: flex
                        .form-group
                            margin-right: 1rem
                            flex-direction: column
                            &.calculated
                                margin: 0 1rem 0 0
                                label
                                    display: block
                                    padding-top: 0
                                    line-height: 32px
                            &:last-child
                                margin-right: 0

        .form-group
            &.formio-component-fieldset
                margin-bottom: 0.5rem
                flex-wrap: wrap

        .internal,
        .external
            &:not(.formio-component-fieldset)
                display: flex
                align-items: center 
                margin-bottom: 0.5rem
                .col-form-label
                    width: 66%
                    padding-top: 0
                    /* line-height: 32px */
                div[ref=element]
                    width: 100%
            &.formio-component-select 
                select[ref=selectContainer]
                    width: 100%
                input[ref=autocompleteInput], div.formio-errors 
                    display: none
            &.formio-component-day
                display: block
                [ref=autocompleteInput]
                    display: none
        .calculated
            margin-top: 1rem
            margin-bottom: 1rem
            &:first-child
                margin-top: 0
            /* & + .calculated:not(.external)
                margin-top: -0.5rem */
            /* & + .calculated.external
                margin-top: 0 */

            label
                display: none
            input,
            [class^='input-group-']
                border: 0
                box-shadow: none

        .internal-only
            label, .input-group-prepend .input-group-text, .internal-only-note
                color: $internal-only-text-color 

        .formio-component-editgrid
            .editgrid-table-head
                background-color: rgba(black, 0.05)
            .editgrid-table-body
                background-color: white
            .formio-component
                &.form-group
                    width: 100%
                    display: block
                label
                    display: block
                .col-form-label
                    width: 100%
                    padding-top: 0
                    line-height: 32px
                div[ref=element]
                    width: 100%

        .formio-component-number
            input
                text-align: right

        .formio-component-textarea 
            .card.card-body 
                background-color: var(--gray-200) !important 
                padding: 0.5rem

        &.quote-worksheet-portal
            .external
                opacity: 1
                fieldset
                    background-color: rgba(yellow, .2)
                    border: 1px solid rgba(black, .1)

        div.spl-formio-well
            .card.card-body 
                background-color: var(--gray-200) !important
                margin-bottom: 0 !important
                padding: 1rem
</style>
